import { useForm, usePage, router } from '@inertiajs/react';
import React, { useState, useEffect } from 'react';
import FormSection from '@/Blocks/Forms/FormSection';
import ActiveLinkViewer from '@/Components/Surfaces/ActiveLinkViewer';
import { Draggable, Droppable, DragDropContext } from 'react-beautiful-dnd';
import Button from '@/Components/Buttons/Button';
import Modal from '@/Components/Display/Modal';
import Input from '@/Components/Inputs/Input';
import InputSelectGroup from '@/Components/Inputs/InputSelectGroup';
import { useToasts } from '@/Hooks/toastContextProvider';

export default function UpdateCompanyLinksInfomationForm({ company }) {
  const page = usePage();

  const categories = page.props.categories;

  const [profileLinks, setProfileLinks] = useState(company.company_links);
  const [linksToUpdate, setLinksToUpdate] = useState(company.company_links);

  useEffect(() => {
    setLinksToUpdate(linksToUpdate);
    if (profileLinks != linksToUpdate) {
      const newLinksToUpdate = [...linksToUpdate];
      updateOrder();
    }
  }, [linksToUpdate]);

  const [deleteID, setDeleteId] = useState(false);
  const [editLink, setEditLink] = useState(false);

  const { addToast } = useToasts();

  const editLinkForm = useForm({
    _method: 'PUT',
    title: '',
    link_category_id: '',
    url: '',
    id: '',
  });

  const onDragEnd = result => {
    if (result.destination !== null) {
      // Update Links on Front-End

      const updatedLinks = [...profileLinks];
      const [removed] = updatedLinks.splice(result.source.index, 1);
      updatedLinks.splice(result.destination.index, 0, removed);

      setProfileLinks(updatedLinks);

      // Filtered Links to Update on Back-End

      const filteredLinks = updatedLinks.filter(
        l => l.link_category_id == result.source.droppableId,
      );

      setLinksToUpdate(filteredLinks);
    }
  };

  const updateOrder = () => {
    router.put(
      route('company.updateProfileLinksOrder'),
      {
        company_links: linksToUpdate,
      },
      {
        preserveScroll: true,
      },
    );
  };

  const handleEditLink = link => {
    editLinkForm.setData(data => ({ ...data, title: link.title }));
    editLinkForm.setData(data => ({ ...data, id: link.id }));
    editLinkForm.setData(data => ({
      ...data,
      link_category_id: link.link_category_id,
    }));
    editLinkForm.setData(data => ({ ...data, url: link.url }));

    setEditLink(true);
  };

  const updateLink = () => {
    editLinkForm.put(route('company.updateProfileLink'), {
      errorBag: 'updateProfileLink',
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => handleEditSuccess(),
    });
  };

  const handleEditSuccess = () => {
    setEditLink(false);

    router.visit(route('company.show'), {
      only: ['user', 'company'],
      preserveScroll: true,
    });

    addToast({ message: 'Link Updated!', variant: 'success' });
  };

  const handleDeleteLink = link => {
    setDeleteId(link.id);
  };

  const deleteLink = () => {
    router.delete(route('company.deleteProfileLink', deleteID), {
      preserveScroll: true,
      preserveState: false,
      onSuccess: () =>
        addToast({ message: 'Link Deleted!', variant: 'success' }),
    });

    setDeleteId(false);
  };

  return (
    <div>
      <FormSection
        onSubmit={() => updateLinks}
        title={'Your Company Links'}
        description={<p>Edit your active company links below</p>}
      >
        <div className="col-span-2 flex flex-col">
          {profileLinks.length > 0 ? (
            categories.map(category => {
              if (
                profileLinks.filter(
                  link => link.link_category_id == category.id,
                ).length > 0
              ) {
                return (
                  <div className="col-span-2 mb-4" key={category.id}>
                    <h6 className="text-grey">{category.title}</h6>
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId={`${category.id}`}>
                        {(provided, snapshot) => (
                          <ul
                            className="col-span-2 flex flex-col"
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {profileLinks.map((link, index) => {
                              if (link.link_category_id == category.id) {
                                return (
                                  <Draggable
                                    draggableId={link.title}
                                    index={index}
                                    key={link.id}
                                  >
                                    {(provided, snapshot) => (
                                      <div>
                                        <li
                                          className="col-span-2 w-full py-2"
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                        >
                                          <ActiveLinkViewer
                                            nodeId={link.id}
                                            link={link}
                                            editLink={() =>
                                              handleEditLink(link)
                                            }
                                            checkLinkDelete={() =>
                                              handleDeleteLink(link)
                                            }
                                          />
                                        </li>
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              }
                            })}
                            {provided.placeholder}
                          </ul>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                );
              }
            })
          ) : (
            <p className="text-sm text-grey">
              You currently have no active company links.
            </p>
          )}
        </div>
        {deleteID && (
          <Modal
            modalState={true}
            modalHeader="Delete Link"
            modalSubHeader="Are you sure you want to delete this link?"
          >
            <div className="mt-6 grid grid-cols-1 items-center justify-end gap-6 lg:grid-cols-4">
              <div div className="col-span-1 lg:col-span-1">
                <Button
                  style="border"
                  variant="grey"
                  onClick={() => setDeleteId(false)}
                >
                  Cancel
                </Button>
              </div>
              <div className="col-span-1 lg:col-span-3">
                <Button
                  onClick={() => deleteLink()}
                  style="admin"
                  variant="error"
                >
                  Delete Link
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </FormSection>
      {editLink && (
        <Modal
          modalState={true}
          modalHeader="Edit Link"
          modalSubHeader="Update your link information below."
        >
          <div className="-my-6">
            <FormSection
              className=""
              onSubmit={updateLink}
              renderActions={() => (
                <div className="col-span-2">
                  <div className="mt-6 grid grid-cols-1 items-center justify-end gap-6 lg:grid-cols-4">
                    <div className="col-span-1 lg:col-span-1">
                      <Button
                        style="border"
                        variant="grey"
                        onClick={() => setEditLink(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                    <div className="col-span-1 lg:col-span-3">
                      <Button
                        disabled={editLinkForm.processing}
                        type="submit"
                        style="admin"
                        variant="dark"
                      >
                        Save Link
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            >
              {/* <!-- Link Title --> */}
              <div className="col-span-2 lg:col-span-1">
                <Input
                  id="title"
                  label="Link Title"
                  type="text"
                  value={editLinkForm.data.title}
                  onChange={e =>
                    editLinkForm.setData('title', e.currentTarget.value)
                  }
                  error={editLinkForm.errors.title}
                />
              </div>
              {/* <!-- Link Category --> */}
              <div className="col-span-2 lg:col-span-1">
                <InputSelectGroup
                  id="link_category_id"
                  options={categories}
                  label="Link Category"
                  type="text"
                  defaultValue={editLinkForm.data.link_category_id}
                  onChange={e =>
                    editLinkForm.setData(
                      'link_category_id',
                      e.currentTarget.value,
                    )
                  }
                  error={editLinkForm.errors.link_category_id}
                />
              </div>
              {/* <!-- Link URL --> */}
              <div className="col-span-2">
                <Input
                  id="url"
                  label="Link URL"
                  type="text"
                  value={editLinkForm.data.url}
                  onChange={e =>
                    editLinkForm.setData('url', e.currentTarget.value)
                  }
                  error={editLinkForm.errors.url}
                />
              </div>
            </FormSection>
          </div>
        </Modal>
      )}
    </div>
  );
}
